import { useState } from 'react';
import { Link } from 'react-router-dom';
import { aboutContent, siteInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpField = formData.get('phone_alt') as string;
    if (hpField && hpField.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9sv4utlbrt1qcgajaog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'kaiyun.com',
            url: siteInfo.url,
            description: siteInfo.description,
            foundingDate: aboutContent.brandIntro.paragraphs[0].includes('2018') ? '2018' : '',
            contactPoint: {
              '@type': 'ContactPoint',
              email: aboutContent.contact.email,
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${siteInfo.url}/` },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.url}/about` },
            ],
          }),
        }}
      />

      <section className="py-10 md:py-14 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">关于我们</span>
          </nav>
        </div>
      </section>

      <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center">
            {aboutContent.brandIntro.title}
          </h1>
          <div className="mt-8 space-y-5 max-w-3xl mx-auto">
            {aboutContent.brandIntro.paragraphs.map((p, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center">
            {aboutContent.reputation.title}
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {aboutContent.reputation.items.map((item) => (
              <div
                key={item.label}
                className="p-5 rounded-xl bg-background-50 border border-background-200/60 text-center"
              >
                <p className="text-xs text-foreground-500 mb-2">{item.label}</p>
                <p className="text-sm font-semibold text-foreground-900">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 text-center">
            {aboutContent.contact.title}
          </h2>
          <p className="mt-3 text-foreground-600 text-sm text-center">
            {aboutContent.contact.description}
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
              <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.email}</p>
            </div>
            <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                <i className="ri-time-line text-lg"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-1">服务时间</p>
              <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.hours}</p>
            </div>
            <div className="p-5 rounded-xl bg-background-100 border border-background-200/60 text-center">
              <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                <i className="ri-map-pin-line text-lg"></i>
              </div>
              <p className="text-xs text-foreground-500 mb-1">平台性质</p>
              <p className="text-sm font-medium text-foreground-900">{aboutContent.contact.address}</p>
            </div>
          </div>

          <div className="rounded-xl bg-background-100 border border-background-200/60 p-6 md:p-8">
            <h3 className="text-lg font-semibold font-heading text-foreground-900 mb-6">
              发送留言
            </h3>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    您的姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/60 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（不超过500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-hp-field"
              />

              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-send-plane-line mr-2"></i>
                发送留言
              </button>

              {formStatus === 'success' && (
                <div className="p-3 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  <i className="ri-checkbox-circle-line mr-1.5"></i>
                  留言发送成功！我们会尽快回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-3 rounded-lg bg-primary-100 text-primary-800 text-sm">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}